import React, { useState } from "react";
import { api, groupBoutiquesByMarket } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, Flag } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "linen",       label: "Linen"       },
  { key: "supply",      label: "Supply"      },
  { key: "maintenance", label: "Maintenance" },
  { key: "safety",      label: "Safety"      },
  { key: "other",       label: "Other"       },
];

const SEVERITIES = [
  { key: "low",    label: "Low · FYI"     },
  { key: "medium", label: "Medium · Soon" },
  { key: "high",   label: "High · Today"  },
];

const BOUTIQUE_GROUPS = groupBoutiquesByMarket();

export default function FlagIssueDialog({ onClose, onSaved }) {
  const { user } = useAuth();
  const [category, setCategory] = useState("linen");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [boutique, setBoutique] = useState(user?.boutique_code || "PLT");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast.error("Give it a short title."); return; }
    setSaving(true);
    try {
      const res = await api.post("/ops-flags", { category, title, note, severity, boutique_code: boutique });
      toast.success("Flag raised — GM will see it.");
      onSaved?.(res.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not raise flag.");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 mobile-modal-wrap"
      onClick={onClose}
      data-testid="flag-issue-dialog"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto card-shadow mobile-modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 pt-6 pb-2">
          <div className="flex items-center gap-2 label-eyebrow"><Flag size={11} strokeWidth={1.5} /> Quiet heads-up</div>
          <h2 className="font-serif-display text-xl text-stone-900 mt-1">Raise an operational flag</h2>
          <p className="text-xs text-stone-500 mt-1 leading-relaxed">
            Anything from short linen deliveries to a broken table — your GM will see this on their Home feed.
          </p>
          <button onClick={onClose} data-testid="flag-close" className="absolute top-3 right-3 p-2 rounded-full text-stone-400 hover:bg-stone-100">
            <X size={16} strokeWidth={1.5} />
          </button>
        </header>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="label-eyebrow block mb-1.5">Category</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  data-testid={`flag-cat-${c.key}`}
                  className={`px-3 py-1 rounded-full text-xs border ${category === c.key ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"}`}
                >{c.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="flag-title"
              placeholder={category === "linen" ? "Linen delivery short again" : "Short summary of what happened"}
              className="w-full px-4 py-2 rounded-full subtle-border bg-white text-sm outline-none focus:border-stone-400"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Details (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="flag-note"
              rows={3}
              placeholder="A few words to help your GM understand what's needed."
              className="w-full px-4 py-2 rounded-2xl subtle-border bg-white text-sm outline-none focus:border-stone-400 resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1.5">Severity</label>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)} data-testid="flag-severity" className="w-full px-3 py-2 rounded-xl subtle-border text-sm">
                {SEVERITIES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Boutique</label>
              <select value={boutique} onChange={(e) => setBoutique(e.target.value)} data-testid="flag-boutique" className="w-full px-3 py-2 rounded-xl subtle-border text-sm">
                {BOUTIQUE_GROUPS.map(({ market, boutiques }) => (
                  <optgroup key={market} label={market}>
                    {boutiques.map((b) => (
                      <option key={b.code} value={b.code}>{b.code} · {b.shortName}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
        </div>
        <footer className="px-6 py-4 border-t border-stone-100 flex items-center justify-end gap-2">
          <button onClick={onClose} data-testid="flag-cancel" className="px-4 py-2 rounded-full text-sm text-stone-600 hover:bg-stone-100">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="flag-save" className="px-5 py-2 rounded-full text-sm bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50">
            {saving ? "Raising…" : "Raise flag"}
          </button>
        </footer>
      </div>
    </div>
  );
}
